import { inject, Injectable } from '@angular/core';
import { PjmSettlementStateResponseStatus } from '@api/client';
import type { PjmSettlementStateResponse } from '@api/client';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/toast.service';
import { WebSocketController } from '@services/web-socket.controller';
import type { EventWalletTransferUpdated, PjmEscrowUpdatedPayload } from '@services/web-socket.interfaces';
import { filter, firstValueFrom, map, merge, race, skip, take, timer, type Observable } from 'rxjs';

interface PjmSettlementAwareResponse<TData> {
  data?: TData;
  settlement?: PjmSettlementStateResponse;
}

type PjmSettlementContextType = 'freelance_reply' | 'avatar_order';

interface PjmSettlementRefreshFilter {
  businessContextType?: NonNullable<PjmEscrowUpdatedPayload['business_context_type']>;
  businessContextId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PjmSettlementStatusService {
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly webSocketController = inject(WebSocketController);

  private static readonly REFRESH_INTERVAL_MS: number = 5_000;
  private static readonly WAIT_TIMEOUT_MS: number = 30_000;

  public isPending<TData>(response: PjmSettlementAwareResponse<TData> | null | undefined): boolean {
    return response?.settlement?.status === PjmSettlementStateResponseStatus.Pending;
  }

  public contextRefreshSignals(
    contextType: PjmSettlementContextType,
    contextId: number,
    refreshFilter: PjmSettlementRefreshFilter = {},
  ): Observable<void> {
    const matchingWalletEvent = this.webSocketController.walletTransferEvent.pipe(
      filter((event: EventWalletTransferUpdated): boolean => {
        const escrowUpdate = event.escrow_update;

        return !!escrowUpdate
          && escrowUpdate.context_type === contextType
          && escrowUpdate.context_id === contextId
          && event.event_type !== 'payment_intent.submitted'
          && (
            !refreshFilter.businessContextType
            || escrowUpdate.business_context_type === refreshFilter.businessContextType
          )
          && (
            !refreshFilter.businessContextId
            || escrowUpdate.business_context_id === refreshFilter.businessContextId
          );
      }),
      map((): void => undefined),
    );
    const socketReinitialized = this.webSocketController.isSocketInitedSubject.pipe(
      skip(1),
      filter((isInited: boolean): boolean => isInited),
      map((): void => undefined),
    );

    return merge(matchingWalletEvent, socketReinitialized);
  }

  public async showFailure(error: unknown): Promise<void> {
    console.warn('[PjmSettlementStatusService] settlement action failed', error);
    await this.toastService.error(
      this.translate.instant('wallet.businessPayment.settlementFailed')
    );
  }

  public async waitUntilApplied<TData>(
    initialResponse: PjmSettlementAwareResponse<TData>,
    contextType: PjmSettlementContextType,
    contextId: number,
    reload: () => Promise<PjmSettlementAwareResponse<TData>>,
  ): Promise<PjmSettlementAwareResponse<TData>> {
    if (!this.isPending(initialResponse)) {
      return initialResponse;
    }

    await this.toastService.info(
      this.translate.instant('wallet.businessPayment.settlementProcessing')
    );

    let latestResponse: PjmSettlementAwareResponse<TData> = initialResponse;
    const deadline: number = Date.now() + PjmSettlementStatusService.WAIT_TIMEOUT_MS;
    while (this.isPending(latestResponse) && Date.now() < deadline) {
      await this.waitForRefreshSignal(contextType, contextId, deadline);
      try {
        latestResponse = await reload();
      } catch (error) {
        console.warn('[PjmSettlementStatusService] failed to refresh settlement state', error);
      }
    }

    if (this.isPending(latestResponse)) {
      await this.toastService.info(
        this.translate.instant('wallet.businessPayment.settlementDelayed')
      );
    }

    return latestResponse;
  }

  private async waitForRefreshSignal(
    contextType: PjmSettlementContextType,
    contextId: number,
    deadline: number,
  ): Promise<void> {
    const remainingMs: number = Math.max(0, deadline - Date.now());
    const refreshDelayMs: number = Math.min(
      PjmSettlementStatusService.REFRESH_INTERVAL_MS,
      remainingMs
    );
    const matchingWalletEvent = this.webSocketController.walletTransferEvent.pipe(
      filter((event: EventWalletTransferUpdated): boolean => (
        event.escrow_update?.context_type === contextType
        && event.escrow_update.context_id === contextId
      )),
      take(1),
      map((): void => undefined),
    );
    const refreshTimer = timer(refreshDelayMs).pipe(map((): void => undefined));

    await firstValueFrom(race(matchingWalletEvent, refreshTimer));
  }
}
